import { useState, useEffect, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  ChevronRight,
  Clock,
  Tag,
  BookOpen,
  List,
} from 'lucide-react'
import { blogPosts, blogCategories, type BlogPost } from '@/data/blogPosts'
import { useSEO } from '@/hooks/useSEO'
import { buildAuthorSchema, buildPublisherSchema } from '@/lib/authorSchema'
import { MarketingHeader } from '@/components/marketing/MarketingHeader'
import { MarketingFooter } from '@/components/marketing/MarketingFooter'

function PostCard({ post }: { post: BlogPost }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group flex flex-col rounded-2xl border border-gray-200 bg-white hover:border-blue-200 hover:shadow-lg hover:shadow-blue-50 transition-all duration-300 overflow-hidden"
    >
      <div className="p-6 sm:p-8 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-3">
          <span className="px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">
            {post.category}
          </span>
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <Clock size={12} />
            {post.readTime}
          </span>
        </div>

        <h3 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors leading-snug">
          {post.title}
        </h3>

        <p className="text-sm text-gray-600 leading-relaxed mb-6 flex-1">
          {post.description}
        </p>

        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <Calendar size={12} />
            {new Date(post.publishedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
          <span className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 group-hover:gap-2 transition-all">
            Read
            <ChevronRight size={16} />
          </span>
        </div>
      </div>
    </Link>
  )
}

export function BlogList() {
  const [activeCategory, setActiveCategory] = useState<string | null>(null)

  useSEO({
    title: 'AI Document Processing Blog | Algoscale',
    description: "Explore expert insights on AI document processing, intelligent document automation, OCR vs LLMs, and enterprise workflows with Algoscale's blog.",
    canonical: 'https://documentiq.algoscale.com/blog',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: 'DocumentIQ Blog',
        description: 'Guides and industry insights on AI-powered document extraction.',
        url: 'https://documentiq.algoscale.com/blog',
        publisher: {
          '@type': 'Organization',
          name: 'DocumentIQ',
          url: 'https://documentiq.algoscale.com',
        },
        blogPost: blogPosts.map((p) => ({
          '@type': 'BlogPosting',
          headline: p.title,
          description: p.description,
          url: `https://documentiq.algoscale.com/blog/${p.slug}`,
          datePublished: p.publishedAt,
          author: buildAuthorSchema(p.author),
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://documentiq.algoscale.com' },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://documentiq.algoscale.com/blog' },
        ],
      },
    ],
  })

  const filtered = activeCategory
    ? blogPosts.filter((p) => p.category === activeCategory)
    : blogPosts

  return (
    <div className="min-h-screen bg-white text-gray-900 antialiased">
      <MarketingHeader />

      <section className="py-16 sm:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium mb-6">
            <BookOpen size={14} />
            DocumentIQ Blog
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-900 leading-tight">
            Guides & Insights
          </h1>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            Learn how to extract structured data from documents using AI — guides,
            comparisons, and real-world industry applications.
          </p>
        </div>
      </section>

      <section className="border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 py-4 overflow-x-auto">
            <button
              onClick={() => setActiveCategory(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                activeCategory === null ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All Posts
            </button>
            {blogCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  activeCategory === cat ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((post) => (
              <PostCard key={post.slug} post={post} />
            ))}
          </div>
        </div>
      </section>

      {/* Related from Algoscale */}
      <section className="py-12 bg-gray-50 border-t border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-sm font-semibold text-gray-500 mb-4">More from Algoscale</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: 'AI Consulting Services', url: 'https://algoscale.com/ai-consulting-service/' },
              { title: 'Generative AI Services', url: 'https://algoscale.com/generative-ai-services/' },
              { title: 'Data Engineering', url: 'https://algoscale.com/data-engineering-services/' },
              { title: 'Algoscale Blog', url: 'https://algoscale.com/blog/' },
            ].map((link) => (
              <a
                key={link.url}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:border-blue-200 hover:text-blue-600 transition-colors"
              >
                {link.title} &rarr;
              </a>
            ))}
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}

export function BlogPost() {
  const { slug } = useParams<{ slug: string }>()
  const post = blogPosts.find((p) => p.slug === slug)

  useSEO({
    title: post?.title || 'Blog Post',
    description: post?.description || 'DocumentIQ blog post',
    canonical: post ? `https://documentiq.algoscale.com/blog/${post.slug}` : undefined,
    ogType: 'article',
    ogImage: post ? `/og/blog/${post.slug}.png` : undefined,
    jsonLd: post ? [
      {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.description,
        datePublished: post.publishedAt,
        dateModified: post.publishedAt,
        keywords: post.tags.join(', '),
        articleSection: post.category,
        author: buildAuthorSchema(post.author),
        publisher: buildPublisherSchema(),
        mainEntityOfPage: `https://documentiq.algoscale.com/blog/${post.slug}`,
        image: `https://documentiq.algoscale.com/og/blog/${post.slug}.png`,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://documentiq.algoscale.com' },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://documentiq.algoscale.com/blog' },
          { '@type': 'ListItem', position: 3, name: post.category, item: `https://documentiq.algoscale.com/blog/${post.slug}` },
        ],
      },
    ] : undefined,
  })

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Post not found.</p>
          <Link to="/blog" className="text-blue-600 font-medium hover:underline">View all posts</Link>
        </div>
      </div>
    )
  }

  const currentIndex = blogPosts.indexOf(post)
  const nextPost = blogPosts[(currentIndex + 1) % blogPosts.length]
  const prevPost = blogPosts[(currentIndex - 1 + blogPosts.length) % blogPosts.length]

  return (
    <div className="min-h-screen bg-white text-gray-900 antialiased">
      <MarketingHeader />

      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/blog" className="hover:text-gray-900 transition-colors flex items-center gap-1">
              <ArrowLeft size={14} /> Blog
            </Link>
            <span>/</span>
            <span className="text-gray-900 font-medium truncate">{post.category}</span>
          </div>
        </div>
      </div>

      <article className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-[1fr_260px] lg:gap-12">
            <div className="max-w-3xl mx-auto lg:mx-0 min-w-0">
              <div className="mb-8">
                <span className="inline-flex px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium mb-4">
                  {post.category}
                </span>
                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 leading-tight mb-4">
                  {post.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-gray-500 flex-wrap">
                  <span className="flex items-center gap-1"><Calendar size={14} /> {new Date(post.publishedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                  <span className="flex items-center gap-1"><Clock size={14} /> {post.readTime}</span>
                  <span>{post.author}</span>
                </div>
              </div>

              <div className="prose prose-gray prose-headings:font-semibold prose-headings:tracking-tight prose-headings:scroll-mt-24 prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline prose-img:rounded-xl prose-table:border prose-th:border prose-th:px-3 prose-th:py-2 prose-th:bg-gray-50 prose-td:border prose-td:px-3 prose-td:py-2 max-w-none">
                <ReactMarkdown
                  components={{
                    h2: ({ children }) => <h2 id={slugify(String(children))}>{children}</h2>,
                    h3: ({ children }) => <h3 id={slugify(String(children))}>{children}</h3>,
                    a: ({ href, children }) => {
                      const isInternal = href?.startsWith('/') || href?.startsWith('https://documentiq.algoscale.com')
                      return isInternal ? (
                        <Link to={href?.replace('https://documentiq.algoscale.com', '') || '#'}>{children}</Link>
                      ) : (
                        <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
                      )
                    },
                  }}
                >
                  {post.content.replace(/^# .+\n+/, '')}
                </ReactMarkdown>
              </div>

              <div className="mt-8 flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span key={tag} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs">
                    <Tag size={12} /> {tag}
                  </span>
                ))}
              </div>
            </div>

            <aside className="hidden lg:block">
              <TableOfContents content={post.content} />
            </aside>
          </div>
        </div>
      </article>

      {/* Related from Algoscale */}
      <section className="border-t border-gray-200 bg-gray-50 py-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-sm font-semibold text-gray-500 mb-4">Related from Algoscale</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { title: 'AI Consulting', url: 'https://algoscale.com/ai-consulting-service/' },
              { title: 'Generative AI', url: 'https://algoscale.com/generative-ai-services/' },
              { title: 'Text Analytics', url: 'https://algoscale.com/text-analytics/' },
            ].map((link) => (
              <a key={link.url} href={link.url} target="_blank" rel="noopener noreferrer" className="px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:border-blue-200 hover:text-blue-600 transition-colors">
                {link.title} &rarr;
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Prev/Next */}
      <section className="border-t border-gray-200 bg-white py-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between">
          {prevPost && prevPost.slug !== post.slug && (
            <Link to={`/blog/${prevPost.slug}`} className="group text-left">
              <span className="text-xs text-gray-400">Previous</span>
              <p className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors mt-1">{prevPost.title}</p>
            </Link>
          )}
          <div />
          {nextPost && nextPost.slug !== post.slug && (
            <Link to={`/blog/${nextPost.slug}`} className="group text-right">
              <span className="text-xs text-gray-400">Next</span>
              <p className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors mt-1">{nextPost.title}</p>
            </Link>
          )}
        </div>
      </section>

      <section className="py-12 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Ready to try it yourself?</h2>
          <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-blue-600 font-medium hover:bg-blue-50 transition-colors shadow-md text-sm">
            Start for Free <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}


function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
}

interface TocItem {
  level: number
  text: string
  id: string
}

function TableOfContents({ content }: { content: string }) {
  const toc = useMemo<TocItem[]>(() => {
    const items: TocItem[] = []
    const lines = content.split('\n')
    let inCodeBlock = false
    for (const line of lines) {
      if (line.startsWith('```')) {
        inCodeBlock = !inCodeBlock
        continue
      }
      if (inCodeBlock) continue
      const m = line.match(/^(##|###)\s+(.+)$/)
      if (m) {
        const level = m[1].length
        const text = m[2].replace(/\*\*/g, '').replace(/`/g, '').trim()
        items.push({ level, text, id: slugify(text) })
      }
    }
    return items
  }, [content])

  const [activeId, setActiveId] = useState<string | null>(null)

  useEffect(() => {
    if (toc.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible.length > 0) {
          setActiveId(visible[0].target.id)
        }
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 }
    )

    toc.forEach((item) => {
      const el = document.getElementById(item.id)
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [toc])

  if (toc.length < 2) return null

  return (
    <nav className="sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2">
      <div className="flex items-center gap-2 mb-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
        <List size={14} />
        On this page
      </div>
      <ul className="space-y-1 text-sm border-l border-gray-200">
        {toc.map((item) => (
          <li key={item.id} style={{ paddingLeft: item.level === 3 ? '1rem' : '0' }}>
            <a
              href={`#${item.id}`}
              onClick={(e) => {
                e.preventDefault()
                const el = document.getElementById(item.id)
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  window.history.pushState(null, '', `#${item.id}`)
                }
              }}
              className={`block py-1 pl-4 -ml-px border-l-2 transition-colors ${
                activeId === item.id
                  ? 'border-blue-600 text-blue-600 font-medium'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-400'
              } ${item.level === 3 ? 'text-xs' : ''}`}
            >
              {item.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  )
}
